import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

import stepBack from "@assets/images/stepsBack.jpg";
import { Card } from "@mui/material";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { sleepSliderData } from "data/uiData/SleepRanksData";
import { LockSleepCoach } from "@utils/defaultImages";
import { mindfulnessSliderData } from "data/uiData/MindfulnessSliderData";

const SleepCoachRanks = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();

  // Get unlocked badges from location state or empty array
  const unlockedBadges = location?.state?.unlockedBadges || [];
  const mindfulness = location?.state?.mindfulness;
  const sliderData = mindfulness ? mindfulnessSliderData : sleepSliderData;
  // Separate achieved and unachieved milestones
  const achievedMilestones = sliderData?.filter((badge) =>
    unlockedBadges?.some((unlocked) => unlocked.badge_name === badge.title),
  );

  const unachievedMilestones = sliderData?.filter(
    (badge) =>
      !unlockedBadges?.some((unlocked) => unlocked.badge_name === badge.title),
  );

  const renderMilestoneSlider = (data, isAchieved, title, uniqueId) => (
    <div className="mb-6 [@media(max-width:639px)]:!max-w-[70vw] sm:max-w-[100%]">
      <h5 className="text-[16px] font-[Lato] text-white mb-3 ml-2">{title}</h5>
      <Swiper
        modules={[Navigation]}
        spaceBetween={20}
        slidesPerView={1}
        breakpoints={{
          0: { slidesPerView: 1 },
          700: { slidesPerView: 2, spaceBetween: 15 },
          1024: { slidesPerView: 2, spaceBetween: 20 },
          1280: { slidesPerView: 3, spaceBetween: 20 },
        }}
        navigation={{
          nextEl: `.swiper-button-next-${uniqueId}`,
          prevEl: `.swiper-button-prev-${uniqueId}`,
        }}
        className="all-meals !px-[22px] sm:!px-[20px] !py-[25px] rounded-[10px] relative"
      >
        <div
          className={`swiper-button-prev-${uniqueId} swiper-button-prev after:!text-[20px] after:!font-[600] !bg-white !rounded-full !w-9 !h-9 sm:!w-10 sm:!h-10 !flex !items-center !justify-center !shadow-md after:!text-[#0575e6] absolute top-1/2 !-left-0 z-10`}
        ></div>
        <div
          className={`swiper-button-next-${uniqueId} swiper-button-next !bg-white after:!text-[20px] after:!font-[600] !rounded-full !w-9 !h-9 sm:!w-10 sm:!h-10 !flex !items-center !justify-center !shadow-md after:!text-[#0575e6] absolute top-1/2 !-right-0 z-10`}
        ></div>

        {data?.map((badge, index) => {
          // Find if this badge is unlocked
          const unlockedBadge = unlockedBadges?.find(
            (unlocked) => unlocked.badge_name === badge.title,
          );

          // Get unlock date if exists
          const unlockDate = unlockedBadge?.unlocked_at
            ? new Date(unlockedBadge.unlocked_at).toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            : null;

          return (
            <SwiperSlide key={index} className="!h-auto">
              <div className="flex flex-col justify-center w-full h-full relative">
                <div
                  className={`${
                    !isAchieved && "blur-[8px]"
                  } bg-white rounded-3xl w-full max-w-[80%] p-5 relative pb-8 mx-auto flex flex-col h-full`}
                >
                  <div className="flex-grow flex flex-col justify-between">
                    <div className="flex justify-center mt-[30%]">
                      <h3 className="text-[#000] text-[12px] sm:text-[16px] font-[600] font-[Lato] text-center">
                        {t(
                          `${mindfulness ? "mindfulnessRanks" : "sleepRanks"}.${badge?.title}.title`,
                        )}
                      </h3>
                    </div>

                    <div className="flex justify-center">
                      <button
                        disabled
                        className={`bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-[22px] py-2 px-3 text-[10px] sm:text-[12px] my-2 ${
                          isAchieved ? "text-white" : "text-gray-400"
                        }`}
                      >
                        {isAchieved
                          ? unlockDate
                            ? `Unlocked on ${unlockDate}`
                            : t("quizGame.Unlocked")
                          : t("quizGame.Locked")}
                      </button>
                    </div>

                    <div className="border border-[#DDDDDD] p-2 rounded-xl mb-auto">
                      <p className="text-[10px] md:text-[12px] text-[#5C5C5C] text-center">
                         {t(
                          `${mindfulness ? "mindfulnessRanks" : "sleepRanks"}.${badge?.title}.description`,
                        )}
                      </p>
                    </div>
                  </div>

                  <img
                    src={badge.RankImage}
                    alt="image"
                    className="absolute -top-[8%] left-1/2 -translate-x-1/2 z-[9] w-[40%] object-cover"
                    style={{ maxHeight: "none" }}
                  />
                </div>

                {!isAchieved && (
                  <img
                    src={LockSleepCoach}
                    alt="Locked"
                    className="absolute top-[40%] 2xl:top-[30%] left-1/2 -translate-x-1/2 z-[10] object-cover w-[40px] sm:w-[50px]"
                  />
                )}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );

  return (
    <>
      <div className="flex justify-end">
        <button
          className="bg-[#64DDFF] py-[12px] px-6 text-white rounded-full mb-2 flex items-center gap-2"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft
            className="w-6 h-6 bg-white rounded-full text-[#4CDBFF] p-[2px]"
            strokeWidth={4}
          />
          {t("quizGame.Return To Dashboard")}
        </button>
      </div>

      <Card className="p-5 !rounded-xl">
        <div
          className="p-6 w-full rounded-xl text-white bg-cover h-full relative flex flex-col items-center sm:block"
          style={{ backgroundImage: `url(${stepBack})` }}
        >
          {/* Show only if there are achieved milestones */}
          {achievedMilestones?.length > 0 &&
            renderMilestoneSlider(
              achievedMilestones,
              true,
              t("quizGame.Your Achieved Milestones"),
              "milestone-achieved",
            )}

          {/* Show only if there are unachieved milestones */}
          {unachievedMilestones?.length > 0 &&
            renderMilestoneSlider(
              unachievedMilestones,
              false,
              t("quizGame.Milestones Yet to Achieve"),
              "milestone-unachieved",
            )}

          {/* Message when no badges exist */}
          {sliderData?.length === 0 && (
            <div className="text-center py-10">
              <p className="text-white text-lg">
                {t("No milestones available yet")}
              </p>
            </div>
          )}
        </div>
      </Card>
    </>
  );
};

export default SleepCoachRanks;
